import { createContext, useContext, type ReactNode } from "react";
import { useUploadQueue, type UploadQueueApi } from "./useUploadQueue";

/**
 * UploadQueueProvider — hoists a single `useUploadQueue` instance above
 * the route tree so the queue (and, crucially, its in-memory `File`
 * objects) survives in-app navigation.
 *
 * Without this the queue lived inside `AdminMatchesPage`; navigating to
 * another admin section unmounted the hook, dropped every `File`, and
 * forced the operator to re-pick each video on return — even though the
 * files were still alive in the JS heap. Mounting the provider in
 * `AdminShell` keeps the hook (and the active upload's chunk loop)
 * running across `/admin/*` route changes; the docked `UploadQueuePanel`
 * renders once at shell level.
 *
 * A hard browser refresh still loses the `File` objects — that's an
 * unavoidable browser constraint — and falls back to the localStorage
 * "re-pick to resume" path. This provider only fixes the SPA-navigation
 * case, where losing the files was gratuitous.
 */

const UploadQueueContext = createContext<UploadQueueApi | null>(null);

export function UploadQueueProvider({ children }: { children: ReactNode }) {
  const queue = useUploadQueue();
  return (
    <UploadQueueContext.Provider value={queue}>
      {children}
    </UploadQueueContext.Provider>
  );
}

/** Access the shell-level upload queue. Throws if called outside an
 * `<UploadQueueProvider>` — a wiring mistake, not a runtime condition. */
export function useUploadQueueContext(): UploadQueueApi {
  const ctx = useContext(UploadQueueContext);
  if (ctx === null) {
    throw new Error(
      "useUploadQueueContext must be used within an <UploadQueueProvider>",
    );
  }
  return ctx;
}
